import {useTheme} from '@mui/material';
import {HomeEnum, QueryParamsEnum} from 'presentation/constants';
import {useSearchProcedureQuery} from 'presentation/modules/guide/shared/queries/useSearchProcedureQuery';
import {UseProcedureQueryProps} from 'presentation/modules/guide/shared/queries/useSearchProcedureQuery/types';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {useDebounce} from 'presentation/pages/private/hooks/useDebounce';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
	HandleIsOptionEqualToValueProps,
	HandleOnChangeProps,
	HandleOnHighlightChangeProps,
	HandleOnInputChangeProps,
} from '../../SearchProcedure/types';
import {translations} from 'presentation/translations-new';

const {searchProcedure} = translations['pt-br'].privateFlow.guideFlow;

export function useSearchProcedure() {
	const theme = useTheme();
	const navigate = useNavigate();
	const [openErrorModal, setOpenErrorModal] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [value, setValue] = useState<UseProcedureQueryProps['value'] | null>(null);
	const debouncedSearch = useDebounce({value: inputValue, delay: 600});
	const {setSearchParams} = useQueryParams();
	const minChars = 2;

	const handleOpenErrorModal = () => {
		setOpenErrorModal(true);
	};

	const handleCloseErrorModal = () => {
		setOpenErrorModal(false);
	};

	const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError} =
		useSearchProcedureQuery({debouncedSearch, inputValue, value, minChars});

	const options = useMemo(() => {
		const fetchedOptions = data?.pages.flatMap(page => page?.content ?? []) ?? [];
		if (!value) return fetchedOptions;

		const hasValue = fetchedOptions.some(opt => opt.nM_PROCEDIMENTO === value.nM_PROCEDIMENTO);
		if (hasValue) return fetchedOptions;

		return [value, ...fetchedOptions];
	}, [data, value]);

	const getNoOptionsText = useCallback(() => {
		if (debouncedSearch.length < minChars) return searchProcedure.typeAtLeastTwoChars;
		return searchProcedure.noResults;
	}, [debouncedSearch.length, minChars]);

	const handleIsOptionEqualToValue = useCallback(
		({opt, val}: HandleIsOptionEqualToValueProps) => {
			return opt?.nM_PROCEDIMENTO === val?.nM_PROCEDIMENTO;
		},
		[],
	);

	const handleOnChange = useCallback(
		({event: _, newValue}: HandleOnChangeProps) => {
			setValue(newValue);
			setSearchParams(prev => {
				const next = new URLSearchParams(prev);
				next.set(QueryParamsEnum.PROCEDURE, newValue?.cD_PROCEDIMENTO || '');
				next.set(QueryParamsEnum.SPECIALTY_NAME, newValue?.nM_PROCEDIMENTO || '');
				return next;
			});
		},
		[setSearchParams],
	);

	const handleOnInputChange = useCallback(
		({event: _, newInputValue}: HandleOnInputChangeProps) => {
			setInputValue(newInputValue);
		},
		[],
	);

	const handleGetOptionLabel = useCallback((option: UseProcedureQueryProps['value']) => {
		if (!option) return '';
		return option.nM_PROCEDIMENTO;
	}, []);

	const handleOnHighlightChange = useCallback(
		({event: _, option}: HandleOnHighlightChangeProps) => {
			if (!option) return;

			const optionsLength = options.length;
			if (optionsLength === 0) return;

			const isLastItem = options.indexOf(option) === optionsLength - 1;
			if (isLastItem && hasNextPage && !isFetchingNextPage) {
				fetchNextPage();
			}
		},
		[options, hasNextPage, isFetchingNextPage, fetchNextPage],
	);

	const handleGoToHome = useCallback(() => {
		navigate(HomeEnum.HOME_PAGE);
	}, [navigate]);

	useEffect(() => {
		if (isError) {
			handleOpenErrorModal();
		}
	}, [isError]);

	return {
		theme,
		openErrorModal,
		inputValue,
		value,
		options,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		handleCloseErrorModal,
		handleOnChange,
		handleOnInputChange,
		handleGetOptionLabel,
		handleOnHighlightChange,
		handleIsOptionEqualToValue,
		handleGoToHome,
		fetchNextPage,
		getNoOptionsText,
	};
}
